import { HTMLAttributes } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

import { DashboardButton } from "../Button/DashboardButton";

import { useAutocompletePaperContext } from "./AutocompletePaperContext";
import { AutocompleteOption } from "./types";
import { useTagRendering } from "./useTagRendering";

import { useTranslate } from "@/hooks/useTranslate";

interface DefaultAutocompletePaperProps extends HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
}

export const DefaultAutocompletePaper = ({ children, ...rest }: DefaultAutocompletePaperProps) => {
  const { t } = useTranslate();
  const {
    multiple,
    internalValue,
    syncedInternalValue,
    internalIsOpen,
    usingSubmitPattern,
    externalRenderTags,
    onApply,
    onClear,
    onRemoveTag,
    chipProps,
  } = useAutocompletePaperContext();

  const handleApply = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onApply();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClear();
  };

  // Create a mock getTagProps function for rendering tags in the paper
  const createGetTagProps = (value: (string | AutocompleteOption)[]) => (options: { index: number }) => ({
    key: options.index,
    ["data-tag-index"]: options.index,
    tabIndex: -1 as const,
    onDelete: value[options.index] ? (_event: unknown) => onRemoveTag(value[options.index]) : (_event: unknown) => {},
  });

  const { renderTags: internalRenderTags } = useTagRendering({
    tags: multiple ? (internalValue as (string | AutocompleteOption<string>)[]) || [] : [],
    ignoreInputWidth: true,
    chipProps,
  });

  const renderTags = externalRenderTags || internalRenderTags;

  // Show tags in the paper when open and using submit pattern
  const shouldShowTagsInPaper = internalIsOpen && usingSubmitPattern && multiple && renderTags;
  const tagsToRender =
    shouldShowTagsInPaper && Array.isArray(internalValue)
      ? renderTags(
          internalValue as (string | AutocompleteOption)[],
          createGetTagProps(internalValue as (string | AutocompleteOption)[])
        )
      : null;

  return (
    <Paper
      {...rest}
      sx={{ marginBlockStart: 1, minWidth: "250px" }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {shouldShowTagsInPaper && tagsToRender && (
        <Box
          sx={{
            padding: 1.5,
            boxSizing: "border-box",
            minHeight: "49px",
            borderBlockEnd: (theme) => `1px solid ${theme.palette.border.neutral.quiet}`,
          }}
        >
          {tagsToRender}
        </Box>
      )}
      {children}
      <Stack
        direction="row-reverse"
        justifyContent="flex-start"
        gap={1}
        padding={1.5}
        sx={{ borderBlockStart: (theme) => `1px solid ${theme.palette.border.neutral.quiet}` }}
      >
        <DashboardButton
          variant="tonal"
          vibe="brand"
          onMouseDown={handleApply}
          disabled={
            multiple
              ? (internalValue as unknown as AutocompleteOption<string>[])?.length === 0 &&
                (syncedInternalValue as unknown as AutocompleteOption<string>[])?.length === 0
              : !internalValue
          }
          data-test="autocomplete-apply-button"
        >
          {t("dashboard", "dynamicFilter.apply")}
        </DashboardButton>
        <DashboardButton variant="text" vibe="brand" onMouseDown={handleClear} data-test="autocomplete-clear-button">
          {t("dashboard", "dynamicFilter.clear")}
        </DashboardButton>
      </Stack>
    </Paper>
  );
};
